import { DateInput } from '@progress/kendo-dateinputs-common'

/**
 * Parche al DateInput de Kendo: ignorar los `keydown` que no vienen del teclado.
 *
 * `DateInput.onElementKeyDown` (núcleo `@progress/kendo-dateinputs-common`, el que usan DatePicker,
 * DateInput y DateRangePicker) da por hecho que todo `keydown` es un `KeyboardEvent` y hace
 * `e.keyCode.toString()` y `e.key.toLowerCase()` sin comprobar nada. Cuando un script despacha un
 * `Event('keydown')` a pelo sobre el input —extensiones del navegador, autocompletados— no hay
 * `keyCode` ni `key`, y revienta con «Cannot read properties of undefined (reading 'toString')».
 *
 * Pasó en producción el 7-sep-2026 en Órdenes de trabajo > Generadas (Bug 43060): las dos fechas del
 * filtro fallaban en el mismo milisegundo, señal de que el evento se repartió a todos los inputs de
 * la página y no de una tecla pulsada. La 0.4.12 de Kendo sigue igual.
 *
 * Kendo engancha el manejador en el constructor (`this.onElementKeyDown.bind(this)`), así que el
 * parche va sobre el prototipo y tiene que aplicarse antes de que exista el primer DateInput: por eso
 * se importa desde los providers raíz. Un keydown sin tecla no significa nada para un campo de fecha,
 * así que se descarta sin más; los `KeyboardEvent` de verdad siguen el camino de siempre.
 */

const MARCA = '__xtsIgnoraKeydownSinTecla'

type PrototipoDateInput = DateInput & { [MARCA]?: true }

/** Un keydown de teclado trae `keyCode` numérico y `key` de texto: es lo que Kendo da por hecho. */
export const esKeydownDeTeclado = (evento: unknown): evento is KeyboardEvent =>
  typeof evento === 'object' &&
  evento !== null &&
  typeof (evento as KeyboardEvent).keyCode === 'number' &&
  typeof (evento as KeyboardEvent).key === 'string'

/** Aplica el parche una sola vez, aunque el módulo se vuelva a evaluar (HMR, pruebas). */
export const ignorarKeydownSinTeclaEnDateInput = (): void => {
  const prototipo = DateInput.prototype as PrototipoDateInput

  if (prototipo[MARCA]) {
    return
  }

  const original = prototipo.onElementKeyDown

  prototipo.onElementKeyDown = function (this: DateInput, evento: unknown): void {
    if (!esKeydownDeTeclado(evento)) {
      return
    }
    original.call(this, evento)
  }
  prototipo[MARCA] = true
}

ignorarKeydownSinTeclaEnDateInput()
